import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import { loadCachedValue, saveCachedValue, subscribeToCachedValue, upsertSupabaseSingleton } from "@/integrations/supabase/contentStore";
import { defaultAdSettings, normalizeAdSettings, type AdSettings } from "@/lib/adSettings";

const STORAGE_KEY = "afiliabr_site_settings";
const DEFAULT_DEALS_HERO_IMAGE_URL =
  "https://readdy.ai/api/search-image?query=vibrant%20sale%20shopping%20deals%20discount%20products%20electronics%20gadgets%20colorful%20abstract%20background%20with%20price%20tags%20and%20lightning%20bolts%20energy%20dynamic%20composition&width=1400&height=340&seq=dealshero&orientation=landscape";

export type SiteSettings = {
  siteName: string;
  slogan: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  initialMenuBackgroundColor: string;
  initialMenuTextColor: string;
  initialMenuActiveTextColor: string;
  fixedMenuBackgroundColor: string;
  fixedMenuTextColor: string;
  fixedMenuActiveTextColor: string;
  heroTitle: string;
  heroSubtitle: string;
  dealsHeroImageUrl: string;
  homeDealsDescription: string;
  homeFeaturedDescription: string;
  guidesPageDescription: string;
  footerText: string;
  socialLinks: {
    instagram: string;
    youtube: string;
    tiktok: string;
    facebook: string;
  };
  seoDescription: string;
  seoKeywords: string;
  adSettings: AdSettings;
};

type SiteSettingsRow = {
  id: string;
  site_name: string;
  slogan: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  initial_menu_background_color: string;
  initial_menu_text_color: string;
  initial_menu_active_text_color: string;
  fixed_menu_background_color: string;
  fixed_menu_text_color: string;
  fixed_menu_active_text_color: string;
  hero_title: string;
  hero_subtitle: string;
  deals_hero_image_url?: string | null;
  home_deals_description?: string | null;
  home_featured_description?: string | null;
  guides_page_description?: string | null;
  footer_text: string;
  social_links: Partial<SiteSettings["socialLinks"]> | null;
  seo_description: string;
  seo_keywords: string;
  ad_settings: Partial<AdSettings> | null;
};

export const defaultSettings: SiteSettings = {
  siteName: "AfiliaBR",
  slogan: "Os melhores produtos com os melhores preços",
  logoUrl: "https://public.readdy.ai/ai/img_res/3f2f095b-9985-4601-9265-8f102d76ede6.png",
  faviconUrl: "",
  primaryColor: "#f97316",
  secondaryColor: "#1c1917",
  accentColor: "#ea580c",
  initialMenuBackgroundColor: "#1c1917",
  initialMenuTextColor: "#fdba74",
  initialMenuActiveTextColor: "#fb923c",
  fixedMenuBackgroundColor: "#ffffff",
  fixedMenuTextColor: "#334155",
  fixedMenuActiveTextColor: "#f97316",
  heroTitle: "Encontre o Produto Perfeito pelo Melhor Preço",
  heroSubtitle: "Reviews honestos, comparativos detalhados e as melhores ofertas de afiliados — tudo em um só lugar.",
  dealsHeroImageUrl: DEFAULT_DEALS_HERO_IMAGE_URL,
  homeDealsDescription: "Somente os produtos habilitados em /admin/ofertas aparecem aqui sem alterar o layout atual",
  homeFeaturedDescription: "Somente os produtos habilitados no painel /admin/destaques aparecem aqui automaticamente",
  guidesPageDescription: "Análises honestas, comparativos detalhados e guias completos para você comprar com segurança e economizar.",
  footerText: "© 2024 AfiliaBR. Todos os direitos reservados. Este site contém links de afiliados.",
  socialLinks: {
    instagram: "#",
    youtube: "#",
    tiktok: "#",
    facebook: "#",
  },
  seoDescription: "Seu guia definitivo para encontrar os melhores produtos com os melhores preços.",
  seoKeywords: "afiliados, produtos, ofertas, reviews, comparativos",
  adSettings: defaultAdSettings,
};

function normalizeOptionalText(value: string | null | undefined, fallback: string) {
  return value == null ? fallback : String(value).trim();
}

function normalizeSettings(value: Partial<SiteSettings> | null | undefined): SiteSettings {
  return {
    ...defaultSettings,
    ...value,
    heroTitle: normalizeOptionalText(value?.heroTitle, defaultSettings.heroTitle),
    heroSubtitle: normalizeOptionalText(value?.heroSubtitle, defaultSettings.heroSubtitle),
    dealsHeroImageUrl: String(value?.dealsHeroImageUrl ?? defaultSettings.dealsHeroImageUrl).trim() || defaultSettings.dealsHeroImageUrl,
    homeDealsDescription: normalizeOptionalText(value?.homeDealsDescription, defaultSettings.homeDealsDescription),
    homeFeaturedDescription: normalizeOptionalText(value?.homeFeaturedDescription, defaultSettings.homeFeaturedDescription),
    guidesPageDescription: normalizeOptionalText(value?.guidesPageDescription, defaultSettings.guidesPageDescription),
    socialLinks: {
      ...defaultSettings.socialLinks,
      ...(value?.socialLinks ?? {}),
    },
    adSettings: normalizeAdSettings(value?.adSettings),
  };
}

function mapRow(row: SiteSettingsRow | null): SiteSettings {
  return normalizeSettings(
    row
      ? {
          siteName: row.site_name,
          slogan: row.slogan,
          logoUrl: row.logo_url,
          faviconUrl: row.favicon_url,
          primaryColor: row.primary_color,
          secondaryColor: row.secondary_color,
          accentColor: row.accent_color,
          initialMenuBackgroundColor: row.initial_menu_background_color,
          initialMenuTextColor: row.initial_menu_text_color,
          initialMenuActiveTextColor: row.initial_menu_active_text_color,
          fixedMenuBackgroundColor: row.fixed_menu_background_color,
          fixedMenuTextColor: row.fixed_menu_text_color,
          fixedMenuActiveTextColor: row.fixed_menu_active_text_color,
          heroTitle: row.hero_title,
          heroSubtitle: row.hero_subtitle,
          dealsHeroImageUrl: row.deals_hero_image_url ?? defaultSettings.dealsHeroImageUrl,
          homeDealsDescription: row.home_deals_description ?? defaultSettings.homeDealsDescription,
          homeFeaturedDescription: row.home_featured_description ?? defaultSettings.homeFeaturedDescription,
          guidesPageDescription: row.guides_page_description ?? defaultSettings.guidesPageDescription,
          footerText: row.footer_text,
          socialLinks: row.social_links ? { ...defaultSettings.socialLinks, ...row.social_links } : defaultSettings.socialLinks,
          seoDescription: row.seo_description,
          seoKeywords: row.seo_keywords,
          adSettings: normalizeAdSettings(row.ad_settings),
        }
      : defaultSettings
  );
}

function mapToRow(settings: SiteSettings): SiteSettingsRow {
  return {
    id: "default",
    site_name: settings.siteName,
    slogan: settings.slogan,
    logo_url: settings.logoUrl,
    favicon_url: settings.faviconUrl,
    primary_color: settings.primaryColor,
    secondary_color: settings.secondaryColor,
    accent_color: settings.accentColor,
    initial_menu_background_color: settings.initialMenuBackgroundColor,
    initial_menu_text_color: settings.initialMenuTextColor,
    initial_menu_active_text_color: settings.initialMenuActiveTextColor,
    fixed_menu_background_color: settings.fixedMenuBackgroundColor,
    fixed_menu_text_color: settings.fixedMenuTextColor,
    fixed_menu_active_text_color: settings.fixedMenuActiveTextColor,
    hero_title: settings.heroTitle,
    hero_subtitle: settings.heroSubtitle,
    deals_hero_image_url: settings.dealsHeroImageUrl,
    home_deals_description: settings.homeDealsDescription,
    home_featured_description: settings.homeFeaturedDescription,
    guides_page_description: settings.guidesPageDescription,
    footer_text: settings.footerText,
    social_links: settings.socialLinks,
    seo_description: settings.seoDescription,
    seo_keywords: settings.seoKeywords,
    ad_settings: settings.adSettings,
  };
}

function loadSettings(): SiteSettings {
  return normalizeSettings(loadCachedValue<SiteSettings>(STORAGE_KEY, defaultSettings));
}

function saveSettings(settings: SiteSettings): void {
  saveCachedValue(STORAGE_KEY, normalizeSettings(settings));
}

async function fetchFromSupabase() {
  const { data, error } = await supabase.from("site_settings").select("*").limit(1).maybeSingle();

  if (error) {
    throw error;
  }

  return (data ?? null) as SiteSettingsRow | null;
}

async function syncToSupabase(settings: SiteSettings) {
  const row = await upsertSupabaseSingleton({
    table: "site_settings",
    row: mapToRow(settings),
  });

  return mapRow((row ?? null) as SiteSettingsRow | null);
}

export function applyColorsToDom(settings: SiteSettings) {
  const root = document.documentElement;
  root.style.setProperty("--color-primary", settings.primaryColor);
  root.style.setProperty("--color-secondary", settings.secondaryColor);
  root.style.setProperty("--color-accent", settings.accentColor);
}

export function useSiteSettings() {
  const { session } = useSessionContext();
  const [settings, setSettings] = useState<SiteSettings>(loadSettings);

  useEffect(() => {
    applyColorsToDom(settings);
  }, [settings]);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEY, defaultSettings, (value) => {
      const next = normalizeSettings(value as SiteSettings);
      setSettings(next);
      applyColorsToDom(next);
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remoteRow = await fetchFromSupabase();
        if (!active) return;

        if (remoteRow) {
          const remote = mapRow(remoteRow);
          setSettings(remote);
          saveSettings(remote);
          applyColorsToDom(remote);
          return;
        }

        const cached = loadSettings();
        if (!session) return;

        const seeded = await syncToSupabase(cached);

        if (!active) return;

        setSettings(seeded);
        saveSettings(seeded);
        applyColorsToDom(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const persist = useCallback((next: SiteSettings) => {
    const normalized = normalizeSettings(next);
    setSettings(normalized);
    saveSettings(normalized);
    applyColorsToDom(normalized);

    void syncToSupabase(normalized)
      .then((remote) => {
        setSettings(remote);
        saveSettings(remote);
        applyColorsToDom(remote);
      })
      .catch(() => {
        // ignore and keep cache fallback
      });
  }, []);

  const updateSettings = useCallback(
    (partial: Partial<SiteSettings>) => {
      persist({
        ...settings,
        ...partial,
        socialLinks: {
          ...settings.socialLinks,
          ...(partial.socialLinks ?? {}),
        },
        adSettings: normalizeAdSettings({
          ...settings.adSettings,
          ...(partial.adSettings ?? {}),
          providers: {
            ...settings.adSettings.providers,
            ...(partial.adSettings?.providers ?? {}),
            adsense: {
              ...settings.adSettings.providers.adsense,
              ...(partial.adSettings?.providers?.adsense ?? {}),
            },
            gam: {
              ...settings.adSettings.providers.gam,
              ...(partial.adSettings?.providers?.gam ?? {}),
            },
            monetag: {
              ...settings.adSettings.providers.monetag,
              ...(partial.adSettings?.providers?.monetag ?? {}),
            },
            ezoic: {
              ...settings.adSettings.providers.ezoic,
              ...(partial.adSettings?.providers?.ezoic ?? {}),
            },
            mediavine: {
              ...settings.adSettings.providers.mediavine,
              ...(partial.adSettings?.providers?.mediavine ?? {}),
            },
          },
          slots: {
            ...settings.adSettings.slots,
            ...(partial.adSettings?.slots ?? {}),
          },
        }),
      });
    },
    [persist, settings]
  );

  const resetToDefault = useCallback(() => {
    persist(defaultSettings);
  }, [persist]);

  return { settings, updateSettings, resetToDefault };
}

export function getSiteSettings(): SiteSettings {
  return loadSettings();
}